import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../contexts/LanguageContext";
import styles from "./Navbar.module.css";

function Navbar() {
  const [userData, setUserData] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [t] = useTranslation("global");
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleChangeLang = () => {
    const newLang = lang === "en" ? "ar" : "en";
    setLang(newLang);
  };

  useEffect(() => {
    const storedUserData = JSON.parse(localStorage.getItem("userDataLogin"));
    if (storedUserData) {
      setUserData(storedUserData);
    }
  }, []);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
      setShowScrollToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userDataLogin");
    setUserData(null);
    toast.success("Logged out successfully!");
    navigate("/");
  };

  const handleClick = () => {
    const userData = JSON.parse(localStorage.getItem("userDataLogin"));
    const userType = userData?.user?.type;
    const targetPath = userType === "student" ? "/profileDetails" : "/profile";
    navigate(targetPath);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <nav className={`${styles.navbar} ${isScrolled ? styles.navbarScrolled : ''}`}>
        <div className={styles.navbarContainer}>
          <Link to='/' className={styles.navbarBrand}>
            <img
              src="/assets/imgs/logo3.png"
              alt="Logo"
              className={styles.navbarLogo}
            />
          </Link>

          <ul className={styles.navbarNav}>
            <li className={styles.navItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                } 
                to="/"
              >
                {t("header.home")}
              </NavLink>
            </li>
            <li className={styles.navItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                } 
                to="/courses"
              >
                {t("header.courses")}
              </NavLink>
            </li>
            {userData?.user?.type !== "teacher" && (
              <li className={styles.navItem}>
                <NavLink 
                  className={({ isActive }) => 
                    `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                  } 
                  to="/instructors"
                >
                  {t("header.inustructos")}
                </NavLink>
              </li>
            )}
            <li className={styles.navItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                } 
                to="/about-us"
              >
                {t("header.about")}
              </NavLink>
            </li>
            <li className={styles.navItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                } 
                to="/articles"
              >
                {t("header.articles")}
              </NavLink>
            </li>
          </ul>

          <div className={styles.desktopActions}>
            <button 
              className={styles.languageToggle} 
              onClick={handleChangeLang}
            >
              <i className="fas fa-globe"></i>
              {lang === "en" ? "AR" : "EN"}
            </button>

            {userData ? (
              <div className="dropdown">
                <button
                  className="btn dropdown-toggle"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {userData.user.name || "Profile"}
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li onClick={handleClick}>
                    <NavLink className="dropdown-item">{t("header.profile")}</NavLink>
                  </li>
                  <li>
                    <button onClick={handleLogout} className="dropdown-item">
                      {t("header.logout")}
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <NavLink to="/register">
                <button className="btn bg-btn">
                  {t("header.sign")}
                </button>
              </NavLink>
            )}
          </div>

          <button
            className={styles.mobileMenuButton}
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <i className="fas fa-bars"></i>
          </button>
        </div>

        {/* Bootstrap Collapse Mobile Menu */}
        <div className="collapse" id="navbarNav">
          <ul className={styles.mobileNav}>
            <li className={styles.mobileNavItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`
                } 
                to="/"
              >
                {t("header.home")}
              </NavLink>
            </li>
            <li className={styles.mobileNavItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`
                } 
                to="/courses"
              >
                {t("header.courses")}
              </NavLink>
            </li>
            {userData?.user?.type !== "teacher" && (
              <li className={styles.mobileNavItem}>
                <NavLink 
                  className={({ isActive }) => 
                    `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`
                  } 
                  to="/instructors"
                >
                  {t("header.inustructos")}
                </NavLink>
              </li>
            )}
            <li className={styles.mobileNavItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`
                } 
                to="/about-us"
              >
                {t("header.about")}
              </NavLink>
            </li>
            <li className={styles.mobileNavItem}>
              <NavLink 
                className={({ isActive }) => 
                  `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`
                } 
                to="/articles"
              >
                {t("header.articles")}
              </NavLink>
            </li>
            
            {/* Language Toggle */}
            <li className={styles.mobileNavItem}>
              <button 
                className={styles.mobileLanguageToggle} 
                onClick={handleChangeLang}
              >
                <i className="fas fa-globe"></i>
                {lang === "en" ? "العربية" : "English"}
              </button>
            </li>

            {/* User Actions */}
            {userData ? (
              <li className={styles.mobileNavItem}>
                <div className={styles.mobileUserActions}>
                  <span className={styles.mobileUserName}>
                    {userData.user.name || "Profile"}
                  </span>
                  <button 
                    className={styles.mobileProfileBtn}
                    onClick={handleClick}
                  >
                    {t("header.profile")}
                  </button>
                  <button 
                    className={styles.mobileLogoutBtn}
                    onClick={handleLogout}
                  >
                    {t("header.logout")}
                  </button>
                </div>
              </li>
            ) : (
              <li className={styles.mobileNavItem}>
                <NavLink to="/register" className={styles.mobileSignBtn}>
                  {t("header.sign")}
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </nav>

      {/* Scroll to Top Button */}
      <button
        className={`${styles.scrollToTop} ${showScrollToTop ? styles.scrollToTopVisible : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <i className={`fas fa-arrow-up ${styles.scrollToTopIcon}`}></i>
      </button>
    </>
  );
}

export default Navbar;
